import { ref, watch, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useApi } from './useApi';
import { getLocaleFromRoute } from '../router/index.js';

/**
 * Load a published legal document for the current route locale.
 * Uses SSG-embedded `initialState.legal` when present; otherwise fetches from API.
 *
 * @param {import('vue').Ref<string>|string} documentType - privacy_policy | terms_of_service
 */
export function useLegalPublicDocument(documentType) {
  const route = useRoute();
  const api = useApi();
  const initialState = inject('initialState', {});
  const document = ref(null);
  const loading = ref(true);
  const error = ref(null);

  function resolveType() {
    return typeof documentType === 'object' && documentType !== null && 'value' in documentType
      ? documentType.value
      : documentType;
  }

  function applyEmbedded(locale) {
    const embedded = initialState?.legal;
    if (!embedded || embedded.locale !== locale || !embedded.documents) {
      return false;
    }
    const type = resolveType();
    document.value = embedded.documents[type] ?? null;
    loading.value = false;
    error.value = null;
    return true;
  }

  async function load() {
    const locale = getLocaleFromRoute(route);
    const type = resolveType();

    if (applyEmbedded(locale)) {
      return;
    }

    loading.value = true;
    error.value = null;
    try {
      const res = await api.get(`/legal/${locale}`);
      document.value = res.data?.documents?.[type] ?? null;
    } catch (e) {
      error.value = e.message || 'LOAD_ERROR';
      document.value = null;
    } finally {
      loading.value = false;
    }
  }

  watch(
    () => [route.params.lang, resolveType()],
    () => {
      void load();
    },
    { immediate: true }
  );

  return { document, loading, error, reload: load };
}
